import {
    HttpClient,
    HttpParams,
    HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { recoverNotFound } from '../utils/api-error.utils';
import {
    BilledType,
    BillingHistoryRequest,
    BillingHistoryResponse,
} from '../models/billing-history.models';
import {
    OfficeDetailDto,
    PagedResult,
    PracticeAreaDto,
    ResourceAttributesDto,
    ResourceDetailDto,
    ResourceDto,
    ResourceListRequest,
    ResourceTagCategory,
    ResourceTagDto,
    ResourceTimeOffExportLayout,
    ResourceTimeOffRequest,
    ResourceTimeOffResultDto,
} from '../models/resource.models';
import { VisibilityResourceOverrideDto } from '../models/user.models';

@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/resources`;

  private readonly tagsChangedSubject = new Subject<void>();

  /**
   * Emits after a resource tag is added or removed anywhere in the app. Views
   * that derive state from the tag set — notably the resource list's attribute
   * filter dropdowns, which read the visibility-scoped distinct-tags endpoint
   * once — subscribe to refresh without waiting for a page reload.
   */
  readonly tagsChanged$ = this.tagsChangedSubject.asObservable();

  /**
   * Get filtered and paginated list of resources
   */
  getResources(
    request: ResourceListRequest,
  ): Observable<PagedResult<ResourceDto>> {
    const body: Record<string, unknown> = {
      page: request.page,
      pageSize: request.pageSize,
    };

    this.appendResourceFilters(body, request);
    if (request.sortBy) {
      body['sortBy'] = request.sortBy;
    }
    if (request.sortDirection) {
      body['sortDirection'] = request.sortDirection;
    }
    if (request.excludePracticeAreaCode) {
      body['excludePracticeAreaCode'] = request.excludePracticeAreaCode;
    }

    return this.http
      .post<PagedResult<ResourceDto>>(`${this.apiUrl}/search`, body);
  }

  /**
   * Adds the resource directory filters shared by every resource search body — the free-text
   * search plus the six multi-select filters — and then the attribute filters.
   *
   * This block used to be copy-pasted verbatim into getResources, getResourcesWithBilling and
   * buildTimeOffRequestBody, so widening the class-year filter to `classYears` meant making the
   * same edit in three places and one miss would have silently dropped the filter on that endpoint
   * alone. Callers layer their own extras (paging, sorting, dates) around this call; body key order
   * does not matter to the API.
   */
  private appendResourceFilters(
    body: Record<string, unknown>,
    request: ResourceListRequest,
  ): void {
    if (request.search) {
      body['search'] = request.search;
    }
    if (request.titles && request.titles.length > 0) {
      body['titles'] = request.titles;
    }
    if (request.classYears && request.classYears.length > 0) {
      body['classYears'] = request.classYears;
    }
    if (request.offices && request.offices.length > 0) {
      body['offices'] = request.offices;
    }
    if (request.practiceAreaCodes && request.practiceAreaCodes.length > 0) {
      body['practiceAreaCodes'] = request.practiceAreaCodes;
    }
    if (request.capacityStatusCodes && request.capacityStatusCodes.length > 0) {
      body['capacityStatusCodes'] = request.capacityStatusCodes;
    }
    if (request.seniorityCodes && request.seniorityCodes.length > 0) {
      body['seniorityCodes'] = request.seniorityCodes;
    }
    this.appendAttributeFilters(body, request);
  }

  /**
   * Adds the resource profile attribute filters (skills, jurisdictions, languages,
   * interests, sub-practice areas) to a search request body when present.
   */
  private appendAttributeFilters(
    body: Record<string, unknown>,
    request: ResourceListRequest,
  ): void {
    if (request.skillCodes && request.skillCodes.length > 0) {
      body['skillCodes'] = request.skillCodes;
    }
    if (request.jurisdictionCodes && request.jurisdictionCodes.length > 0) {
      body['jurisdictionCodes'] = request.jurisdictionCodes;
    }
    if (request.languageCodes && request.languageCodes.length > 0) {
      body['languageCodes'] = request.languageCodes;
    }
    if (request.interestCodes && request.interestCodes.length > 0) {
      body['interestCodes'] = request.interestCodes;
    }
    if (request.subPracticeAreaCodes && request.subPracticeAreaCodes.length > 0) {
      body['subPracticeAreaCodes'] = request.subPracticeAreaCodes;
    }
  }

  /**
   * Get every resource matching the filters, each enriched with billing-hours totals
   * for the given date range. Returns the full matched set (no server-side paging or
   * sorting) so the resource list can sort and paginate by any billing-hours type
   * client-side. Heavier than getResources; used only by the resource directory list.
   */
  getResourcesWithBilling(
    request: ResourceListRequest,
    startDate: string,
    endDate: string,
  ): Observable<ResourceDto[]> {
    const body: Record<string, unknown> = {
      page: request.page,
      pageSize: request.pageSize,
      startDate,
      endDate,
    };

    this.appendResourceFilters(body, request);

    return this.http
      .post<ResourceDto[]>(`${this.apiUrl}/search-with-billing`, body);
  }

  /**
   * Search active employees without applying the current user's practice-area
   * access filter. Used for the requestor picker on request create/edit so any
   * employee can be selected as a requestor regardless of practice area.
   */
  searchAllResources(
    request: ResourceListRequest,
  ): Observable<PagedResult<ResourceDto>> {
    const body: Record<string, unknown> = {
      page: request.page,
      pageSize: request.pageSize,
    };

    if (request.search) {
      body['search'] = request.search;
    }
    if (request.sortBy) {
      body['sortBy'] = request.sortBy;
    }
    if (request.sortDirection) {
      body['sortDirection'] = request.sortDirection;
    }

    return this.http
      .post<PagedResult<ResourceDto>>(`${this.apiUrl}/search-all`, body);
  }

  /**
   * Get resource by employee ID
   */
  getResource(employeeId: string): Observable<ResourceDto> {
    return this.http.get<ResourceDto>(`${this.apiUrl}/${employeeId}`);
  }

  /**
   * Search resources by name for @mention typeahead
   * Returns top 5 matching resources
   */
  searchResourcesForMention(searchTerm: string): Observable<ResourceDto[]> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    const body = {
      page: 1,
      pageSize: 5,
      search: searchTerm.trim(),
    };

    return this.http
      .post<PagedResult<ResourceDto>>(`${this.apiUrl}/search`, body)
      .pipe(
        map((result) => result.items || []),
      );
  }

  /**
   * Get resource capacity statistics for dashboard
   */
  getResourceStats(): Observable<{
    aboveCapacity: number;
    atCapacity: number;
    belowCapacity: number;
    unknown: number;
    notAvailable: number;
  }> {
    return this.http
      .get<{
        aboveCapacity: number;
        atCapacity: number;
        belowCapacity: number;
        unknown: number;
        notAvailable: number;
      }>(`${this.apiUrl}/stats`);
  }

  /**
   * Get current user's allowed practice areas for filtering
   */
  getUserPracticeAreas(): Observable<PracticeAreaDto[]> {
    return this.http.get<PracticeAreaDto[]>(`${this.apiUrl}/practice-areas`);
  }

  /**
   * Get all practice areas (from PracticeAreasController)
   */
  getAllPracticeAreas(): Observable<PracticeAreaDto[]> {
    return this.http
      .get<PracticeAreaDto[]>(`${environment.apiUrl}/api/practiceareas`);
  }

  /**
   * Get unique office values for filtering
   * Returns office DTOs with code and description
   */
  getUniqueOffices(): Observable<
    {
      code: string;
      description: string;
      active: boolean;
      resourceCount: number;
      letterCode: string | null;
      country: string | null;
    }[]
  > {
    return this.http
      .get<
        {
          code: string;
          description: string;
          active: boolean;
          resourceCount: number;
          letterCode: string | null;
          country: string | null;
        }[]
      >(`${this.apiUrl}/offices`)
      .pipe(
        map((offices) => offices.filter((o) => !!o.code)),
      );
  }

  /**
   * Get detailed office information by office code
   */
  getOfficeDetail(officeCode: string): Observable<OfficeDetailDto> {
    return this.http
      .get<OfficeDetailDto>(
        `${this.apiUrl}/offices/${encodeURIComponent(officeCode)}`,
      );
  }

  /**
   * Get unique title values for filtering
   */
  getUniqueTitles(): Observable<string[]> {
    return this.http
      .get<{ count: number; titles: string[] }>(`${this.apiUrl}/titles`)
      .pipe(
        map((response) => response.titles || []),
      );
  }

  /**
   * Get unique class year values for filtering. Viewer-scoped like titles: a
   * year is offered only if at least one visible resource has it. Newest first.
   */
  getUniqueClassYears(): Observable<string[]> {
    return this.http
      .get<{ count: number; classYears: string[] }>(`${this.apiUrl}/class-years`)
      .pipe(
        map((response) => response.classYears || []),
      );
  }

  /**
   * Get resource details by employee ID from the API
   */
  getResourceDetails(employeeId: string): Observable<ResourceDetailDto> {
    return this.http.get<ResourceDto>(`${this.apiUrl}/${employeeId}`).pipe(
      map((resource: ResourceDto) => {
        // Convert ResourceDto to ResourceDetailDto by adding computed fields
        const detailDto: ResourceDetailDto = {
          ...resource,
          // Generate avatar URL based on name and employeeId for consistency
          avatarUrl: this.generateAvatarUrl(
            resource.firstName,
            resource.employeeId,
          ),
        };
        return detailDto;
      }),
    );
  }

  /**
   * Get a resource's profile attributes (skills, jurisdictions, languages,
   * interests, sub-practice areas) by employee ID, grouped by category.
   * Requires Resources.Read and the resource to be visible to the caller.
   */
  getResourceAttributes(employeeId: string): Observable<ResourceAttributesDto> {
    return this.http
      .get<ResourceTagDto[]>(`${this.apiUrl}/${employeeId}/tags`)
      .pipe(
        map((tags) => this.groupTags(tags)),
      );
  }

  /**
   * Get the current user's own profile attributes, grouped by category
   * (no special permission required).
   */
  getMyAttributes(): Observable<ResourceAttributesDto> {
    return this.http
      .get<ResourceTagDto[]>(`${this.apiUrl}/my/tags`)
      .pipe(
        map((tags) => this.groupTags(tags)),
      );
  }

  /**
   * Add a tag to the current user's own resource (no special permission required).
   * The tag lookup value is created server-side if the name is new.
   */
  addMyTag(
    category: ResourceTagCategory,
    tagName: string,
  ): Observable<ResourceTagDto> {
    return this.http
      .post<ResourceTagDto>(`${this.apiUrl}/my/tags`, { category, tagName })
      .pipe(
        tap(() => this.tagsChangedSubject.next()),
      );
  }

  /**
   * Add a tag to another resource (requires Resources.Write and visibility of the
   * target resource). The tag lookup value is created server-side if the name is new.
   */
  addResourceTag(
    employeeId: string,
    category: ResourceTagCategory,
    tagName: string,
  ): Observable<ResourceTagDto> {
    return this.http
      .post<ResourceTagDto>(`${this.apiUrl}/${employeeId}/tags`, {
        category,
        tagName,
      })
      .pipe(
        tap(() => this.tagsChangedSubject.next()),
      );
  }

  /**
   * Remove a tag from the current user's own resource. Only the association is
   * removed; the tag value remains available for reuse.
   */
  removeMyTag(lookupValueId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/my/tags/${lookupValueId}`)
      .pipe(
        tap(() => this.tagsChangedSubject.next()),
      );
  }

  /**
   * Remove a tag from another resource (requires Resources.Write and visibility
   * of the target resource).
   */
  removeResourceTag(
    employeeId: string,
    lookupValueId: number,
  ): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/${employeeId}/tags/${lookupValueId}`)
      .pipe(
        tap(() => this.tagsChangedSubject.next()),
      );
  }

  /**
   * Get the distinct tags attached to resources within the caller's visibility
   * scope (requires Resources.Read). Powers the resource-list attribute filters.
   */
  getVisibleTags(): Observable<ResourceTagDto[]> {
    return this.http.get<ResourceTagDto[]>(`${this.apiUrl}/tags`);
  }

  /**
   * Groups a flat tag list into the category-keyed shape the attributes card
   * consumes. Every category is initialized so empty responses stay safe.
   */
  private groupTags(tags: ResourceTagDto[]): ResourceAttributesDto {
    const grouped: ResourceAttributesDto = {
      skills: [],
      jurisdictions: [],
      languages: [],
      interests: [],
      subPracticeAreas: [],
    };
    const keyByCategory: Record<
      ResourceTagCategory,
      keyof ResourceAttributesDto
    > = {
      Skill: 'skills',
      Jurisdiction: 'jurisdictions',
      Language: 'languages',
      Interest: 'interests',
      SubPractice: 'subPracticeAreas',
    };

    for (const tag of tags ?? []) {
      const key = keyByCategory[tag.category];
      if (key) {
        grouped[key].push({
          code: tag.code,
          name: tag.name,
          lookupValueId: tag.lookupValueId,
        });
      }
    }

    return grouped;
  }

  /**
   * Set (create or update) a resource's explicit visibility override to the given
   * RESOURCE_VISIBILITY_MODE code. Admin-only (Admin.VisibilityOverrides.Write).
   */
  setResourceVisibility(
    employeeId: string,
    modeCode: string,
  ): Observable<VisibilityResourceOverrideDto> {
    return this.http
      .put<VisibilityResourceOverrideDto>(
        `${this.apiUrl}/visibility/${employeeId}`,
        { modeCode },
      );
  }

  /**
   * Clear a resource's visibility override so it falls back to the system default.
   * Admin-only (Admin.VisibilityOverrides.Write). Treats a 404 (nothing to clear)
   * as success, since the desired end state — no override — already holds.
   */
  clearResourceVisibility(employeeId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/visibility/${employeeId}`)
      .pipe(recoverNotFound(undefined));
  }

  /**
   * Generate avatar URL based on name and employeeId for consistency
   */
  private generateAvatarUrl(firstName: string, employeeId: string): string {
    // Use a deterministic approach to generate consistent avatars
    const gender =
      firstName.toLowerCase().endsWith('a') ||
      [
        'sarah',
        'lisa',
        'jennifer',
        'jessica',
        'emily',
        'michelle',
        'laura',
        'karen',
        'nancy',
        'betty',
        'helen',
        'sandra',
        'donna',
        'carol',
        'ruth',
        'sharon',
        'deborah',
        'dorothy',
        'amy',
        'angela',
        'ashley',
        'brenda',
        'emma',
        'olivia',
        'cynthia',
        'marie',
        'janet',
        'catherine',
        'frances',
        'christine',
        'samantha',
        'debra',
        'rachel',
        'carolyn',
        'virginia',
        'maria',
        'heather',
        'diane',
        'julie',
        'joyce',
        'victoria',
        'kelly',
        'christina',
        'joan',
        'evelyn',
        'lauren',
        'judith',
        'megan',
        'andrea',
        'cheryl',
        'hannah',
      ].includes(firstName.toLowerCase())
        ? 'women'
        : 'men';
    // Generate a number from the employeeId string for consistency
    const idHash = employeeId
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const avatarNumber = (idHash % 99) + 1;
    return `https://randomuser.me/api/portraits/${gender}/${avatarNumber}.jpg`;
  }

  /**
   * Get billing history for a resource
   * @param employeeId The employee ID
   * @param startDate Start date (ISO format)
   * @param endDate End date (ISO format)
   * @param billedTypes Optional billed-type filter. Omit to request all types.
   * @returns Observable of billing history summary
   */
  getBillingHistory(
    employeeId: string,
    startDate: string,
    endDate: string,
    billedTypes?: BilledType,
  ): Observable<BillingHistoryResponse> {
    let params = new HttpParams()
      .set('employeeId', employeeId)
      .set('startDate', startDate)
      .set('endDate', endDate);

    if (billedTypes != null) {
      params = params.set('billedTypes', billedTypes);
    }

    return this.http
      .get<BillingHistoryResponse>(`${this.apiUrl}/billing-history`, { params });
  }

  /**
   * Get billing history for multiple resources in a single request
   * @param requests Array of billing history requests with employeeId, startDate, endDate, and optional billedTypes
   * @returns Observable of array of billing history summaries
   */
  getBillingHistoryList(
    requests: BillingHistoryRequest[],
  ): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.apiUrl}/billing-history-list`, requests);
  }

  /**
   * Get time off data for filtered resources (no pagination on resource search).
   * Uses the same filtering as the search endpoint but returns time off entries.
   */
  getFilteredResourcesTimeOff(
    request: ResourceTimeOffRequest,
  ): Observable<ResourceTimeOffResultDto> {
    const body = this.buildTimeOffRequestBody(request);

    return this.http
      .post<ResourceTimeOffResultDto>(
        `${environment.apiUrl}/api/timeoff/resources/search`,
        body,
      );
  }

  /**
   * Export the filtered resource time off data as an .xlsx workbook.
   * Reuses the same filter body as getFilteredResourcesTimeOff and adds the
   * billing date range used to populate the hour-type columns, plus a
   * human-readable timespan label shown in those columns' headers.
   * exportType picks the workbook layout: 'grouped' (parent + sub-header +
   * collapsible children) or 'flattened' (every row carries full data).
   */
  exportResourceTimeOff(
    request: ResourceTimeOffRequest,
    billingStartDate: string | null,
    billingEndDate: string | null,
    billingTimespanLabel: string | null,
    exportType: ResourceTimeOffExportLayout,
    onlyResourcesWithTimeOff = false,
  ): Observable<HttpResponse<Blob>> {
    const body: Record<string, unknown> = {
      ...this.buildTimeOffRequestBody(request),
      exportType,
      onlyResourcesWithTimeOff,
    };
    if (billingStartDate) {
      body['billingStartDate'] = billingStartDate;
    }
    if (billingEndDate) {
      body['billingEndDate'] = billingEndDate;
    }
    if (billingTimespanLabel) {
      body['billingTimespanLabel'] = billingTimespanLabel;
    }

    return this.http.post(
      `${environment.apiUrl}/api/resources/time-off/export`,
      body,
      { responseType: 'blob', observe: 'response' }
    );
  }

  private buildTimeOffRequestBody(
    request: ResourceTimeOffRequest,
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {};

    this.appendResourceFilters(body, request);
    if (request.startDate) {
      body['startDate'] = request.startDate;
    }
    if (request.endDate) {
      body['endDate'] = request.endDate;
    }
    if (request.statusCodes && request.statusCodes.length > 0) {
      body['statusCodes'] = request.statusCodes;
    }
    if (request.timeFrames && request.timeFrames.length > 0) {
      body['timeFrames'] = request.timeFrames;
    }
    if (request.referenceDate) {
      body['referenceDate'] = request.referenceDate;
    }

    return body;
  }
}
